/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import axios from 'axios'
import { setUrlWithParams } from '@/utils/url'
import { getValidSearchParams, handleThrowError } from '@/utils/fetch'
import { setAuthorizationConfig } from '@/utils/services'
import { licensedAssetsApiUrls } from '@/configs/api-urls/licensed-assets'
import { setOffset } from '@/utils/pagination'

const { base: baseUrl } = licensedAssetsApiUrls

export const getLicensedAssets = async (
  { searchParams, samsState },
  getAccessToken
) => {
  const limit = parseInt(searchParams.limit) || 10
  const page = parseInt(searchParams.page) || 1
  const offset = (page - 1) * limit

  const pagination = { limit, offset }

  const cleanSearchParams = { ...getValidSearchParams(searchParams) }
  delete cleanSearchParams.page
  delete cleanSearchParams.offset
  delete cleanSearchParams.limit

  const config = await setAuthorizationConfig(getAccessToken)
  const url = setUrlWithParams(
    baseUrl,
    {
      ...cleanSearchParams,
      ...pagination,
      samsState
    })

  return axios
    .get(
      url,
      config
    )
    .then(res => res.data)
    .catch(err => handleThrowError('getLicensedAssets', err))
}

export const getLicensedAssetsAsCsv = async (
  { samsState },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = setUrlWithParams(
    `${baseUrl}/reports/csv`,
    { samsState }
  )

  return axios
    .get(
      url,
      config
    )
    .then(res => res.data)
    .catch(err => handleThrowError('getLicensedAssetsAsCsv', err))
}

export const getLicensedAsset = async (
  { licensedAssetId, samsState },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = setUrlWithParams(`${baseUrl}/${licensedAssetId}`, { samsState })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => handleThrowError('getLicensedAsset', err))
}

export const getAIMetadataByLicensedAssetId = async (
  { licensedAssetId, aiMetadataState, searchParams },
  getAccessToken
) => {
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const config = await setAuthorizationConfig(getAccessToken)
  const url = setUrlWithParams(`${baseUrl}/${licensedAssetId}/ai-metadata`, { state: aiMetadataState, ...pagination })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => handleThrowError('getLicensedAsset', err))
}

export const postLicensedAsset = async (ids, getAccessToken) => {
  try {
    const config = await setAuthorizationConfig(getAccessToken)

    const dataToSend = Array.isArray(ids) ? ids[0] : ids

    const response = await axios.post(baseUrl, dataToSend, config)
    return response.data
  } catch (error) {
    handleThrowError('postLicensedAsset', error)
  }
}
